<template>
    <div
        v-if="storageUpdate"
        class="col-sm-12 p-1"
        style="max-width: 80em"
    >
        <h3 class="text-center">
            {{ storageUpdate.name }}
        </h3>
        <span class="span-title ps-2 pe-2">{{ storageUpdate.company.name }}</span>
        <div class="col-sm-12 nopads editor-form ps-2 pe-2 pb-1">
            <div v-if="editable">
                <div class="row">
                    <div class="col-sm-6 nopads">
                        <span class="span-title p-1">{{ $t('materialstorage.name') }}</span>
                        <input
                            v-model="storageUpdate.name"
                            type="text"
                            class="form-control form-control-sm"
                            :disabled="!editable"
                        >
                    </div>
                    <div class="col-sm-6 nopads pt-1">
                        <coordinate-editor
                            v-model="storageUpdate.location"
                            :point-type="MATERIAL_STORAGE"
                            :show-buttons="false"
                            size="sm"
                            :title="$t('materialstorage.location')"
                        />
                    </div>
                </div>
                <div class="col-12 mt-2 ms-2">
                    <div class="form-check form-check-sm">
                        <input
                            :id="'activeCheckbox'"
                            v-model="storageUpdate.active"
                            class="form-check-input"
                            type="checkbox"
                        >
                        <label
                            class="form-check-label text-dark"
                            :for="'activeCheckbox'"
                        >
                            {{ $t('materialstorage.active') }}
                        </label>
                    </div>
                </div>
            </div>
            <h4 class="form-header">
                {{ $t('materialstorage.records') }}
            </h4>
            <material-record-list
                v-model="storageUpdate.material_records"
                :editable="editable"
            />
            <hr>
            <h4 class="form-header">
                {{ $t('materialstorage.share_contracts') }}
            </h4>
            <span class="hint-text"> {{ $t('materialstorage.share_hint') }}</span>
            <material-storage-contracts
                v-model="storageUpdate.contracts"
                class="pb-4"
                :editable="editable"
            />

            <!-- Attachments-->
            <hr>
            <h4 class="form-header">
                {{ $t('materialstorage.attachments') }}
            </h4>
            <div
                v-if="storageUpdate.attachments && storageUpdate.attachments.length"
                class="mb-2"
            >
                <span class="span-title">{{ $t('common.attachments') }}</span>
                <div
                    v-for="item in storageUpdate.attachments"
                    :key="item.id"
                    class="row pointer my-1"
                    @click="downloadAttachment(item)"
                >
                    <div class="col link">
                        {{ item.file_original_name }}
                    </div>
                </div>
            </div>

            <div class="col-12">
                <!-- Show selected files -->
                <div
                    v-for="(file, index) in attachments"
                    :key="index"
                    class="d-flex align-items-center justify-content-between mb-3"
                >
                    <span>{{ file?.name || file }}</span>
                    <button
                        class="btn btn-outline-danger btn-sm"
                        type="button"
                        @click="removeFile(index)"
                    >
                        {{ $t('common.remove') }}
                    </button>
                </div>

                <!-- Hidden file input -->
                <input
                    ref="fileInput"
                    type="file"
                    accept=".pdf, .doc, .docx, .jpg, .jpeg, .png"
                    class="visually-hidden"
                    @change="handleFileChange"
                >
            </div>

            <div class="col-sm-12 button-container pt-3">
                <button
                    class="btn btn-outline-primary btn-sm form-button"
                    type="button"
                    @click="triggerFileInput"
                >
                    {{ $t('import_file_wizard.import_file') }}
                </button>

                <button
                    class="btn btn-sm btn-secondary form-button"
                    @click.stop="$emit('close')"
                >
                    {{ $t('common.close') }}
                </button>
                <button
                    class="btn btn-sm btn-success form-button"
                    :disabled="!editable || !isFormValid || loading > 0"
                    @click.stop="submitStorage()"
                >
                    {{ $t('common.save') }}
                </button>
            </div>
        </div>
        <material-amount-change-modal
            v-if="showAmountChangeModal"
            :changed-records="changedRecords"
            @confirm="onAmountChangeConfirm"
            @cancel="onAmountChangeCancel"
        />
    </div>
</template>

<script>
import CoordinateEditor from '../CoordinateEditor'
import MaterialRecordList from './MaterialRecordList'
import {mapHelper} from '../mixins/MapMixin'
import MaterialStorageContracts from "./MaterialStorageContracts";
import MaterialAmountChangeModal from "./MaterialAmountChangeModal";
import {userHelper} from "@/components/mixins/UserMixin";
import {attachmentHelper} from "@/components/mixins/AttachmentMixin";
import {restApi} from '../mixins/RestApiMixin'
import {inject, toRaw} from "vue";

export default {
    name: 'MaterialStorageEditor',
    components: {MaterialAmountChangeModal, MaterialStorageContracts, MaterialRecordList, CoordinateEditor},
    mixins: [mapHelper, userHelper, attachmentHelper, restApi],
    props: {
        storage: {
            type: Object,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        user: {
            type: Object
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: false,
            storageUpdate: null,
            attachments: [],
            originalAmounts: {},
            showAmountChangeModal: false,
        }
    },
    computed: {
        editable: function () {
            return this.user && this.user.company.id === this.storage.company.id && this.hasObserver(this.user.roles)
        },
        isFormValid() {
            const objectsWithEmptyFields = this.storageUpdate.material_records.filter(item => item.active && ((!item.material_type_id && !item.name)
                || !item.unit));
            return objectsWithEmptyFields.length === 0;
        },
        changedRecords() {
            if (!this.storageUpdate || !this.storageUpdate.material_records) {
                return []
            }
            const result = []
            this.storageUpdate.material_records.forEach((record, index) => {
                if (!record.active) {
                    return
                }
                const key = record.id > 0 ? record.id : ('idx_' + index)
                if (!(key in this.originalAmounts)) {
                    return
                }
                const originalAmount = this.originalAmounts[key]
                if (Number(record.amount) !== Number(originalAmount)) {
                    result.push({ record, originalAmount })
                }
            })
            return result
        }
    },
    mounted() {
        this.storageUpdate = toRaw(this.storage)
        if (this.storageUpdate && this.storageUpdate.material_records) {
            this.storageUpdate.material_records.forEach((record, index) => {
                const key = record.id > 0 ? record.id : 'idx_' + index
                this.originalAmounts[key] = record.amount
            })
        }
    },
    created() {
        // Handle new storages by adding users company as storage company
        if (!this.storage.id || this.storage.id < 1) {
            this.storage.company = this.user.company
        }
    },
    methods: {
        triggerFileInput() {
            this.$refs.fileInput?.click();
        },
        handleFileChange(event) {
            const file = event.target.files[0];
            if (file) {
                this.attachments.push(file);
            }
            // Clear input value so the same file can be selected again if needed
            event.target.value = '';
        },
        removePhoto(index) {
            this.attachments.splice(index, 1);
        },

        onAttachmentChange(event, counter) {
            const file = event.target.files[0];
            this.attachments[counter] = file;
        },

        downloadAttachment: function (item) {
            this.loading = true
            this.restFetch(this.materialStorageAttachmentUrl + this.storage.id + '/' + item.id,
                response => {
                    this.saveFile(item.file_original_name, response.data)
                    this.loading = false
                }, () => {
                    this.loading = false
                })
        },
        removeAttachment: function(item) {
            if(this.storage.attachments && this.storage.attachments.length > 0) {
                var index = this.storage.attachments.findIndex(attachment => attachment.id === item.id)
                if(index >= 0) {
                    this.storage.attachments.splice(index, 1)
                }
            }
        },
        async submitStorage() {
            if(!this.isFormValid) {
                return
            }
            if (this.changedRecords.length > 0) {
                this.showAmountChangeModal = true
                return
            }
            this.doSave(this.storageUpdate)
        },
        onAmountChangeConfirm({ info, contract }) {
            this.showAmountChangeModal = false
            const changedMaterialRecords = this.changedRecords
                .filter(cr => cr.record.id > 0)
                .map(cr => ({
                    id: cr.record.id,
                    name: cr.record.name,
                    info: info,
                    contract: contract ? { id: contract.id, name: contract.name } : null,
                    previousAmount: cr.originalAmount,
                    newAmount: cr.record.amount
                }))
            this.doSave(this.storageUpdate, changedMaterialRecords)
        },
        onAmountChangeCancel() {
            this.showAmountChangeModal = false
        },
        async doSave(storage, changedMaterialRecords = []) {
            this.loading++
            let response = null
            try {
                if (isNaN(storage.id)) {
                    response = await new Promise((resolve, reject) => this.restAdd(this.materialStorageUrl, storage, resolve, reject))
                } else {
                    response = await new Promise((resolve, reject) => this.restUpdate(this.materialStorageUrl, storage, resolve, reject))
                }
                if (changedMaterialRecords.length > 0) {
                    await new Promise((resolve, reject) => this.restAdd(
                        this.materialRecordChangesUrl,
                        changedMaterialRecords,
                        resolve,
                        reject
                    ))
                }
                this.uploadAttachment(response)
            } catch (e) {
                this.showAlert(this.$t('materialstorage.update_failed'))
            }
            this.loading--
        },
        uploadAttachment: function (response) {
            let attachments = this.getValidAttachments(this.attachments)
            if (attachments.length > 0) {
                let formData = new FormData()
                attachments.forEach(file => {
                    if (file !== undefined && file !== null) {
                        formData.append('attachments[]', file)
                    }
                })
                this.restPostWithFile(this.materialStorageAttachmentUrl + response.id, {}, formData,
                    () => {
                        this.loading = false
                        this.$emit('closeAndUpdate')
                    }, this.fail
                )
            } else {
                this.loading = false
                this.$emit('closeAndUpdate')
            }
        },
    }
}
</script>

<style scoped>

</style>
